"use client";
import { useTranslations } from "next-intl"

import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { createClient } from "@/utils/supabase/client";
import AuthModal from "@/components/ui/auth-modal";
import { DialogDescription } from "@/components/ui/dialog";
import { DialogCard } from "@/components/ui/dialog-card";
import { cn } from "@/lib/utils";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { CONTACT_BLOCK_MESSAGE } from "@/lib/contact-policy";
import { getModerationDisplayMessage, getModerationReason } from "@/lib/moderation-client";
import {
  getNormalizedImageFile,
  getSafeImageExtension,
  validateImageFile,
} from "@/lib/image-upload";
import { storageBucket, StoragePath } from "@/lib/storage";
import { useTransition } from "react";
import { ProfileStickyHeader } from "@/components/profile-page/profile-sticky-header";
import { ProfileCoverBand } from "@/components/profile-page/profile-cover-band";
import { ProfileHero } from "@/components/profile-page/profile-hero";
import { ProfileSearchField } from "@/components/profile-page/profile-search-field";
import { ProfileStoreNotice } from "@/components/profile-page/profile-store-notice";
import { useProfileChromeColors } from "@/hooks/use-profile-chrome-colors";
import { filterProfileCollectionsForViewer } from "@/lib/profile-listings-viewer-filter";
import { profilePrimaryName } from "@/lib/profile-utils";
import { getCountryName } from "@/lib/paypal-countries";
import { ProfileTabs } from "@/components/profile-page/profile-tabs";
import { ProfileEditForm } from "@/components/profile-page/profile-edit-form";
import { useMobile } from "@/hooks/use-mobile";
import { useOverlay } from "@/components/context/overlay-context";
import { ChatTabSlider } from "@/components/chat/ChatTabSlider";
import { ChatDialog } from "@/components/chat/ChatDialog";
import { ProfileNotificationModal } from "@/components/profile-page/profile-notification-modal";
import { FollowSuggestionsCarousel } from "@/components/profile-page/follow-suggestions-carousel";
import { toast } from "sonner";
import { ModerationNotice } from "@/components/moderation/ModerationNotice";
import { useProfileListings } from "@/hooks/use-profile-listings";
import {
  getProfileHeaderTabsOpacity,
  PROFILE_COVER_HEIGHT_DESKTOP,
  PROFILE_COVER_HEIGHT_MOBILE,
} from "@/lib/profile-scroll-effects";

interface Collection {
  id: string;
  title: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  display_order: number;
  listings: any[];
}

interface Profile {
  id: string;
  username: string;
  avatar_url: string | null;
  banner_url?: string | null;
  bio: string;
  checkout_note: string;
  followers_count: number;
  following_count: number;
  is_following: boolean;
  banned_at?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: seller's main address country
  // (ISO-2), or `null` for orphan sellers. Replaces the prior heuristic of
  // computing dominant country from `listings[].location`.
  country?: string | null;
}

interface Listing {
  id: string;
  title: string;
  description: string;
  price: string;
  currency?: string | null;
  image_urls: string[];
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: kept for the profile-grid card
  // chain (consumed by `ProfileTabs`/`ListingResults`). The display chip
  // reads `seller.country` at the profile-hero level — this per-listing
  // field is not rendered as a chip on the profile grid.
  shipFromAddress?: { city: string | null; state: string | null; country: string | null } | null;
  condition: string;
  grade: string;
  created_at: string;
  quantity: number;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  type?: "standard" | "preorder" | null;
  user_id?: string;
  // Phase 3 (2026-06-29): `accepts_offers` collapsed — derive via
  // `isOfferEnabledFormat(listing_format)` from `@kraahl/contract`.
  offers_pending_count?: number | null;
}

interface Collection {
  id: string;
  title: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  display_order: number;
  listings: any[];
}

interface Props {
  user: { id: string } | null;
  initialProfile: Profile;
  salesCount: number;
  initialListings: Listing[];
  initialCollections?: Collection[];
  totalListingsCount: number;
  initialListingsHasMore: boolean;
  listingsPageSize: number;
}

export default function ProfileContainer({
  user,
  initialProfile,
  salesCount,
  initialListings,
  initialCollections = [],
  totalListingsCount,
  initialListingsHasMore,
  listingsPageSize,
}: Props) {
  const tProfile = useTranslations("profile");
  const tFollow = useTranslations("follow");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalContext, setAuthModalContext] = useState<"message" | "notification">("notification");

  const isProfileOwner = user?.id === initialProfile.id;
  const [collections, setCollections] = useState<Collection[]>(() =>
    filterProfileCollectionsForViewer(initialCollections, isProfileOwner),
  );

  // Listings pagination — SSR delivers page 1, the hook fetches subsequent
  // pages from /api/listings/by-user when the grid sentinel intersects.
  const {
    listings: paginatedListings,
    hasMore: listingsHasMore,
    isLoading: isLoadingMoreListings,
    loadMore: loadMoreListings,
  } = useProfileListings({
    profileId: initialProfile.id,
    initialListings,
    initialHasMore: initialListingsHasMore,
    pageSize: listingsPageSize,
  });

  // Follow / unfollow
  const [isFollowing, setIsFollowing] = useState(initialProfile.is_following);
  const [followersCount, setFollowersCount] = useState(
    initialProfile.followers_count
  );
  const [followingCount, setFollowingCount] = useState(
    initialProfile.following_count
  );

  // Notification subscription state
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationModalMode, setNotificationModalMode] = useState<"enable" | "disable">("enable");
  const hasCheckedSubscriptionRef = React.useRef<boolean>(false);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      // Don't check if user is viewing their own profile
      if (!user || user.id === initialProfile.id) {
        setIsSubscribed(false);
        setIsLoadingSubscription(false);
        return;
      }

      // Only check once
      if (hasCheckedSubscriptionRef.current) {
        setIsLoadingSubscription(false);
        return;
      }

      setIsLoadingSubscription(true);
      try {
        const response = await fetch(`/api/profile-notifications/subscribe?profileId=${initialProfile.id}`);
        if (response.ok) {
          const data = await response.json();
          setIsSubscribed(data.isSubscribed || false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        hasCheckedSubscriptionRef.current = true;
        setIsLoadingSubscription(false);
      }
    };

    checkSubscription();
  }, [user, initialProfile.id]);

  const handleNotificationClick = async () => {
    if (!user) {
      setAuthModalContext("notification");
      setShowAuthModal(true);
      return;
    }

    if (user.id === initialProfile.id) {
      // Ne pas permettre de s'abonner à son propre profil
      return;
    }

    // Si déjà abonné, ouvrir le modal de désactivation
    if (isSubscribed) {
      setIsNotificationModalOpen(true);
      setNotificationModalMode("disable");
    } else {
      // Si pas abonné, ouvrir le modal de confirmation
      setIsNotificationModalOpen(true);
      setNotificationModalMode("enable");
    }
  };

  const [isPendingNotification, startNotificationTransition] = useTransition();

  const handleConfirmNotification = () => {
    if (!user) {
      setIsNotificationModalOpen(false);
      return;
    }

    // Optimistic update
    const wasSubscribed = isSubscribed;
    if (notificationModalMode === "disable") {
      setIsSubscribed(false);
    } else {
      setIsSubscribed(true);
    }
    setIsNotificationModalOpen(false);

    startNotificationTransition(async () => {
      try {
        if (notificationModalMode === "disable") {
          // Désabonner
          const response = await fetch(`/api/profile-notifications/subscribe?profileId=${initialProfile.id}`, {
            method: 'DELETE',
          });
          if (!response.ok) {
            const error = await response.json();
            console.error('Error unsubscribing from notification:', error);
            // Revert optimistic update + surface the failure (don't leave a
            // stale toggle with no feedback).
            setIsSubscribed(wasSubscribed);
            toast.error(tProfile("notifications.updateError"));
          }
        } else {
          // S'abonner
          const response = await fetch('/api/profile-notifications/subscribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ profileId: initialProfile.id }),
          });

          if (!response.ok) {
            const error = await response.json();
            console.error('Error subscribing to notification:', error);
            // Revert optimistic update + surface the failure (e.g. the
            // require-follow guard rejecting after a concurrent unfollow).
            setIsSubscribed(wasSubscribed);
            toast.error(tProfile("notifications.updateError"));
          }
        }
      } catch (error) {
        console.error('Error in notification action:', error);
        // Revert optimistic update
        setIsSubscribed(wasSubscribed);
        toast.error(tProfile("notifications.updateError"));
      }
    });
  };

  const handleCloseNotificationModal = () => {
    setIsNotificationModalOpen(false);
  };
  const handleFollowClick = (newFollowingState: boolean) => {
    const delta = newFollowingState ? 1 : -1;
    setFollowersCount((c) => c + delta);
    setIsFollowing(newFollowingState);
    // Notifications are a sub-feature of following. The container owns both the
    // bell's isSubscribed state and the post-follow "enable notifications?"
    // prompt (UnifiedFollowButton runs with promptNotificationsOnFollow=false),
    // so the two never desync.
    if (newFollowingState) {
      // onFollowChange fires after /api/follow resolves → the follows row
      // exists, so the subscribe on confirm satisfies the require-follow guard.
      setNotificationModalMode("enable");
      setIsNotificationModalOpen(true);
    } else {
      // Unfollow deletes the subscription server-side (trigger
      // follows_after_delete_unsubscribe). Mirror it so the bell — and a later
      // re-follow — start from the unsubscribed baseline instead of a stale "on".
      setIsSubscribed(false);
    }
  };

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMode, setChatMode] = useState<"tab" | "direct">("tab"); // "tab" pour l'inbox, "direct" pour conversation directe
  const isMobile = useMobile();
  // Spec §E 1.7: mobile-only inline "edit my profile" mode — swaps ProfileTabs
  // for ProfileEditForm in place below (no route change, no dialog/backdrop).
  // Was local `useState` + `dataset.profileEditOpen` read by a
  // bottom-nav-bar-client.tsx MutationObserver (mechanism 4, §B.1) — migrated
  // onto the shared panel slot (`{ kind: "profile-edit" }`,
  // lib/overlay/overlay-state.ts) so nav-bar visibility derives from the FSM
  // like every other panel, same treatment step 1.5 gave feed-details. NOT
  // the `/profile/edit` ROUTE (app/(site)/profile/edit) — that stays
  // pathname-owned, untouched here.
  const { overlay, openPanel, closePanel } = useOverlay();
  const mobileEditOpen = overlay.panel.kind === "profile-edit";

  useEffect(() => {
    if (!isMobile && mobileEditOpen) closePanel();
  }, [isMobile, mobileEditOpen, closePanel]);

  // Unmount-cleanup — same pattern as step 1.5's snap-feed migration
  // (marketplace-vertical-snap-feed.tsx): the panel slot lives in
  // OverlayProvider, above this component's own mount lifecycle. Pre-migration
  // the local `useState` died for free on unmount and the dataset effect's own
  // cleanup deleted the attribute; without this, navigating away from
  // /[username] mid-edit would strand the FSM on "profile-edit", wrongly
  // keeping the bottom nav hidden on whatever page the user lands on next.
  // Guarded on "was THIS panel open", mirroring 1.5's `isMineOpen` guard — a
  // blind `closePanel()` would wrongly close an unrelated panel (e.g. chat)
  // that took the slot after this one closed but before unmount.
  const mobileEditOpenRef = useRef(mobileEditOpen);
  mobileEditOpenRef.current = mobileEditOpen;
  useEffect(() => {
    return () => {
      if (mobileEditOpenRef.current) closePanel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional unmount-only cleanup, see comment above
  }, []);

  const handleOpenMobileEdit = useCallback(() => {
    openPanel({ kind: "profile-edit" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [openPanel]);

  const handleCloseMobileEdit = useCallback(() => {
    closePanel();
  }, [closePanel]);

  const handleInlineProfileUpdated = useCallback(
    (updates: {
      bio?: string;
      checkoutNote?: string;
      avatarUrl?: string | null;
      bannerUrl?: string | null;
    }) => {
      if (updates.bio !== undefined) setBio(updates.bio);
      if (updates.checkoutNote !== undefined) setCheckoutNote(updates.checkoutNote);
      if (updates.avatarUrl !== undefined) setUserImage(updates.avatarUrl);
      if (updates.bannerUrl !== undefined) setBannerUrl(updates.bannerUrl);
    },
    [],
  );

  // Create deterministic room name for chat
  const chatRoomName = useMemo(() => {
    if (!user) return `public:${initialProfile.id}`;
    return [user.id, initialProfile.id].sort().join(":");
  }, [user, initialProfile.id]);

  const handleMessageClick = () => {
    if (!user) {
      setAuthModalContext("message");
      setShowAuthModal(true);
      return;
    }
    
    if (isMobile) {
      // Sur mobile, on ouvre le ChatTabSlider qui va automatiquement ouvrir la conversation
      // Stocker temporairement les infos pour que le TabDrawer ouvre directement la conversation
      setChatMode("direct");
      setChatOpen(true);
    } else {
      // Sur desktop, on ouvre directement le dialog de conversation
      setChatOpen(true);
    }
  };

  // Live profile info
  const [username, setUsername] = useState(initialProfile.username);
  const [userImage, setUserImage] = useState<string | null>(
    initialProfile.avatar_url
  );
  const [bannerUrl, setBannerUrl] = useState<string | null>(
    initialProfile.banner_url ?? null
  );
  const [bio, setBio] = useState(initialProfile.bio);
  const [bioError, setBioError] = useState<string | null>(null);
  const [checkoutNote, setCheckoutNote] = useState(initialProfile.checkout_note);
  const [checkoutNoteError, setCheckoutNoteError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [showImageErrorModal, setShowImageErrorModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const heroRef = useRef<HTMLDivElement>(null);
  const searchRowRef = useRef<HTMLDivElement>(null);
  const tabsRowRef = useRef<HTMLDivElement>(null);
  const [searchCollapseStart, setSearchCollapseStart] = useState(0);
  const [mobileScrollY, setMobileScrollY] = useState(0);
  const mobileScrollRafRef = useRef(0);
const [collectionErrorMessage, setCollectionErrorMessage] = useState<string | null>(null);
const [isCollectionErrorOpen, setIsCollectionErrorOpen] = useState(false);
const collectionDialogMessage = collectionErrorMessage ?? CONTACT_BLOCK_MESSAGE;

const showCollectionError = useCallback((message?: string | null) => {
  const displayMessage = getModerationDisplayMessage(message).message;
  setCollectionErrorMessage(displayMessage);
  setIsCollectionErrorOpen(true);
}, []);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from("profiles")
      .select("username, avatar_url, banner_url, bio, checkout_note")
      .eq("id", initialProfile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setUsername(data.username ?? initialProfile.username);
          setUserImage(data.avatar_url);
          setBannerUrl((data as { banner_url?: string | null }).banner_url ?? null);
          setBio(data.bio ?? "");
          setCheckoutNote((data as { checkout_note?: string | null }).checkout_note ?? "");
        }
      });
  }, [initialProfile.id, initialProfile.username]);

  const handleBioChange = async (newBio: string): Promise<boolean> => {
    if (!user) {
      setShowAuthModal(true);
      return false;
    }
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      return false;
    }
    const nextBio = newBio.trim().slice(0, 150);
    const currentBio = (bio ?? "").trim();
    if (nextBio === currentBio) {
      return true;
    }
    try {
      setBioError(null);
      const response = await fetch("/api/profile/bio", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bio: nextBio }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 422) {
          const display = getModerationDisplayMessage(payload?.error).message;
          const reason = getModerationReason(display) ?? display;
          toast.error(reason);
          setBioError(reason);
          return false;
        }
        const message = payload?.error ?? "Failed to update bio";
        toast.error(message);
        setBioError(message);
        return false;
      }
      setBio(nextBio);
      setBioError(null);
      return true;
    } catch {
      const message = "Failed to update bio";
      toast.error(message);
      setBioError(message);
      return false;
    }
  };

  const handleCheckoutNoteChange = async (newNote: string): Promise<boolean> => {
    if (!user) {
      setShowAuthModal(true);
      return false;
    }
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      return false;
    }
    const next = newNote.trim().slice(0, 500);
    const current = (checkoutNote ?? "").trim();
    if (next === current) {
      return true;
    }
    try {
      setCheckoutNoteError(null);
      const response = await fetch("/api/profile/checkout-note", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkoutNote: next }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 422) {
          const display = getModerationDisplayMessage(payload?.error).message;
          const reason = getModerationReason(display) ?? display;
          toast.error(reason);
          setCheckoutNoteError(reason);
          return false;
        }
        const message =
          (typeof payload?.details === "string" && payload.details.trim().length > 0
            ? payload.details
            : null) ??
          payload?.error ??
          "Failed to update store notice";
        toast.error(message);
        setCheckoutNoteError(message);
        return false;
      }
      setCheckoutNote(next);
      setCheckoutNoteError(null);
      return true;
    } catch {
      const message = "Failed to update store notice";
      toast.error(message);
      setCheckoutNoteError(message);
      return false;
    }
  };

  // Collection title edit handler
  const handleCollectionTitleChange = async (collectionId: string, newTitle: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      return;
    }
    const trimmedTitle = newTitle.trim();
    try {
      const response = await fetch("/api/collections/update", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ collectionId, title: trimmedTitle }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 422) {
          const display = getModerationDisplayMessage(payload?.error).message;
          const reason = getModerationReason(display) ?? display;
          toast.error(reason);
          showCollectionError(display);
          return;
        }
        const message = payload?.error ?? "Failed to update collection title";
        toast.error(message);
        return;
      }
      setCollections((prev) =>
        prev.map((collection) =>
          collection.id === collectionId ? { ...collection, title: trimmedTitle } : collection
        )
      );
    } catch (error) {
      const message = "Failed to update collection title";
      toast.error(message);
      console.error(error);
    }
  };

  // Collection description edit handler
  const handleCollectionDescriptionChange = async (collectionId: string, newDescription: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      return;
    }
    const trimmedDescription = newDescription.trim();
    try {
      const response = await fetch("/api/collections/update", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ collectionId, description: trimmedDescription }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 422) {
          const display = getModerationDisplayMessage(payload?.error).message;
          const reason = getModerationReason(display) ?? display;
          toast.error(reason);
          showCollectionError(display);
          return;
        }
        const message = payload?.error ?? "Failed to update collection description";
        toast.error(message);
        return;
      }
      setCollections((prev) =>
        prev.map((collection) =>
          collection.id === collectionId ? { ...collection, description: trimmedDescription } : collection
        )
      );
    } catch (error) {
      const message = "Failed to update collection description";
      toast.error(message);
      console.error(error);
    }
  };

  const handleListingRemovedFromCollection = useCallback((collectionId: string, listingId: string) => {
    setCollections((prev) =>
      prev.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              listings: (collection.listings || []).filter((l: { id: string }) => l.id !== listingId),
            }
          : collection
      )
    );
  }, []);

  const handleAddItemsToCollection = useCallback(
    async (collectionId: string, listingIds: string[]): Promise<boolean> => {
      if (!user) {
        setShowAuthModal(true);
        return false;
      }
      if (initialProfile.banned_at) {
        toast.error("Your account has been suspended. Please contact support.");
        return false;
      }
      if (listingIds.length === 0) return false;

      const addedListings = paginatedListings.filter((l) => listingIds.includes(l.id));

      setCollections((prev) =>
        prev.map((collection) => {
          if (collection.id !== collectionId) return collection;
          const existingIds = new Set((collection.listings ?? []).map((l: { id: string }) => l.id));
          const merged = [
            ...(collection.listings ?? []),
            ...addedListings
              .filter((l) => !existingIds.has(l.id))
              .map((l) => ({
                id: l.id,
                title: l.title,
                description: l.description,
                price: l.price,
                currency: l.currency ?? null,
                shipFromAddress: l.shipFromAddress ?? null,
                image_urls: l.image_urls,
                created_at: l.created_at,
                condition: l.condition,
                grade: l.grade,
                quantity: l.quantity,
                scheduled_publish_at: l.scheduled_publish_at,
                listing_format: l.listing_format ?? null,
                type: l.type ?? null,
                user_id: l.user_id,
                username,
                avatar_url: userImage ?? undefined,
              })),
          ];
          return { ...collection, listings: merged };
        }),
      );

      try {
        const response = await fetch("/api/collections/add-items", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collectionId, listingIds }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          setCollections((prev) =>
            prev.map((collection) =>
              collection.id === collectionId
                ? {
                    ...collection,
                    listings: (collection.listings ?? []).filter(
                      (l: { id: string }) => !listingIds.includes(l.id),
                    ),
                  }
                : collection,
            ),
          );
          toast.error(typeof payload?.error === "string" ? payload.error : tProfile("tabs.addItemsError"));
          return false;
        }
        toast.success(tProfile("tabs.addItemsSuccess"));
        return true;
      } catch {
        setCollections((prev) =>
          prev.map((collection) =>
            collection.id === collectionId
              ? {
                  ...collection,
                  listings: (collection.listings ?? []).filter(
                    (l: { id: string }) => !listingIds.includes(l.id),
                  ),
                }
              : collection,
          ),
        );
        toast.error(tProfile("tabs.addItemsError"));
        return false;
      }
    },
    [user, initialProfile.banned_at, paginatedListings, username, userImage, tProfile],
  );

  // **implémentation de l'upload d'avatar**
  const handleProfilePictureChangeAction = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setImageError(null);

    // Validate uploaded file (size/type)
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setImageError(validation.error ?? "Invalid image file.");
      setShowImageErrorModal(true);
      e.target.value = "";
      return;
    }

    setUploadingImage(true);
    const supabase = createClient();

    // Normalize file (convert HEIC if needed)
    let normalizedFile = file;
    let previewUrl: string | null = null;
    try {
      const normalized = await getNormalizedImageFile(file);
      normalizedFile = normalized.file;
      previewUrl = normalized.previewUrl;
    } catch (error) {
      console.error("Error normalizing image:", error);
      setImageError("Failed to process the image.");
      setShowImageErrorModal(true);
      setUploadingImage(false);
      e.target.value = "";
      return;
    }

    const fileExt = getSafeImageExtension(normalizedFile);
    const sanitizedExt = fileExt ? fileExt.replace(/[^a-z0-9]/g, "") : "jpg";
    const filePath = StoragePath.avatar(user.id, sanitizedExt);

    // 1) upload dans le bucket "kraahl"
    const { error: uploadError } = await storageBucket(supabase)
      .upload(filePath, normalizedFile, { upsert: true });
    if (uploadError) {
      console.error("Upload error:", uploadError.message);
      setImageError("Échec de l’upload.");
      setShowImageErrorModal(true);
      setUploadingImage(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      e.target.value = "";
      return;
    }

    // 2) `imageUrl` is now the bucket-relative path — the route resolves it for
    // moderation and stores it in profiles.avatar_url; read funnels resolve it.
    // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L4
    const publicUrl: string | undefined = filePath;
    if (!publicUrl) {
      console.error("Aucune URL publique retournée");
      setImageError("Impossible de récupérer l’URL publique.");
      setShowImageErrorModal(true);
      setUploadingImage(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      e.target.value = "";
      return;
    }

    // 3) modération + update via API
    const response = await fetch("/api/profile/avatar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl: publicUrl, storagePath: filePath }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      setImageError(payload?.error ?? "Échec de la mise à jour du profil.");
      setShowImageErrorModal(true);
      setUploadingImage(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      e.target.value = "";
      return;
    }

    // 4) rafraîchissement de l'UI
    setUserImage(publicUrl);
    setUploadingImage(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageError(null);
    setShowImageErrorModal(false);
    e.target.value = "";
  };

  const handleCloseImageErrorModal = () => {
    setShowImageErrorModal(false);
    setImageError(null);
  };

  const resolvedAvatarUrl = getAvatarUrl(userImage);
  const isOwner = isProfileOwner;
  const primaryName = profilePrimaryName({ username });
  const profileListingsSearchPlaceholder = isOwner
    ? tProfile("searchOwnListingsPlaceholder")
    : tProfile("searchUserListingsPlaceholder", { username });
  const profileStickySearchPlaceholder = isOwner
    ? tProfile("searchStickyOwnPlaceholder")
    : tProfile("searchStickyUserPlaceholder", { username });
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: country chip reads the seller's
  // main address (ISO-2) hydrated server-side as `initialProfile.country`.
  // `ProfileHero` expects a display name, so map ISO-2 → name here.
  const countryHint = useMemo(() => {
    const code = initialProfile.country;
    return code ? getCountryName(code) : "";
  }, [initialProfile.country]);
  const { coverPlaceholderReady } = useProfileChromeColors({
    avatarUrl: userImage,
    bannerUrl,
  });

  useEffect(() => {
    if (!isMobile) {
      setMobileScrollY(0);
      return;
    }

    const update = () => {
      mobileScrollRafRef.current = 0;
      setMobileScrollY(window.scrollY);
    };
    const onScroll = () => {
      if (mobileScrollRafRef.current) return;
      mobileScrollRafRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (mobileScrollRafRef.current) cancelAnimationFrame(mobileScrollRafRef.current);
    };
  }, [isMobile]);

  useLayoutEffect(() => {
    const measure = () => {
      const el = searchRowRef.current ?? tabsRowRef.current ?? heroRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setSearchCollapseStart(Math.max(0, Math.round(top)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [bio, checkoutNote, bannerUrl, userImage, username]);

  const handleCollectionCreated = useCallback((collection: { id: string; title: string; listings: any[] }) => {
    setCollections((prev) => [
      ...prev,
      {
        id: collection.id,
        title: collection.title,
        description: "",
        user_id: initialProfile.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        display_order: prev.length + 1,
        listings: collection.listings ?? [],
      },
    ]);
  }, [initialProfile.id]);

  const handleCollectionDeleted = useCallback(async (collectionId: string) => {
    if (initialProfile.banned_at) {
      toast.error("Your account has been suspended. Please contact support.");
      return;
    }

    let collectionToDelete: Collection | undefined;
    setCollections((prev) => {
      collectionToDelete = prev.find((c) => c.id === collectionId);
      return prev.filter((c) => c.id !== collectionId);
    });

    try {
      const response = await fetch(`/api/collections/${collectionId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(
          typeof payload?.error === "string" ? payload.error : "Failed to delete collection",
        );
      }
    } catch (error) {
      if (collectionToDelete) {
        setCollections((prev) => [...prev, collectionToDelete!]);
      }
      toast.error(error instanceof Error ? error.message : "Failed to delete collection");
    }
  }, [initialProfile.banned_at]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredListings = useMemo(() => {
    if (!normalizedSearch) return paginatedListings;
    // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2 (operator confirmation
    // 2026-06-23): drop the `location` clause from the buyer-side search
    // filter — sellers wrote inconsistent free-text formats and the
    // structured country facet is the right shape (out of scope for this ADR).
    return paginatedListings.filter(
      (item) =>
        (item.title ?? "").toLowerCase().includes(normalizedSearch) ||
        (item.description ?? "").toLowerCase().includes(normalizedSearch),
    );
  }, [normalizedSearch, paginatedListings]);

  const filteredCollections = useMemo(() => {
    if (normalizedSearch) return [];
    return collections;
  }, [collections, normalizedSearch]);

  const profileTabListings = useMemo(
    () =>
      filteredListings.map((l) => ({
        id: l.id,
        title: l.title,
        description: l.description,
        price: l.price,
        currency: l.currency ?? null,
        shipFromAddress: l.shipFromAddress ?? null,
        image_urls: l.image_urls,
        created_at: l.created_at,
        condition: l.condition,
        grade: l.grade,
        quantity: l.quantity,
        scheduled_publish_at: l.scheduled_publish_at,
        listing_format: l.listing_format ?? null,
        type: l.type ?? null,
        // Phase 3 (2026-06-29): `accepts_offers` collapsed — derive via
        // `isOfferEnabledFormat(listing_format)`.
        offers_pending_count: l.offers_pending_count ?? null,
        user_id: l.user_id,
        username,
        avatar_url: userImage ?? undefined,
      })),
    [filteredListings, username, userImage]
  );

  const mobileHeaderTabsOpacity = isMobile
    ? getProfileHeaderTabsOpacity(mobileScrollY, PROFILE_COVER_HEIGHT_MOBILE)
    : 0;

  return (
    <>
      <AuthModal
        isOpen={showAuthModal}
        onCloseAction={() => setShowAuthModal(false)}
        title={authModalContext === "message"
          ? tFollow("messageTitle", { username })
          : (username ? tFollow("getNotified", { username }) : undefined)
        }
        username={username}
        userImage={resolvedAvatarUrl ?? undefined}
        message={authModalContext === "message"
          ? tFollow("pleaseLogInMessage")
          : tFollow("getNotifiedDesc")
        }
      />

      <ProfileStickyHeader
        compactTitle={primaryName}
        isOwner={isOwner}
        profileId={initialProfile.id}
        salesCount={salesCount}
        username={username}
        avatarUrl={resolvedAvatarUrl}
        bio={bio}
        currentUserId={user?.id ?? null}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchPlaceholder={profileListingsSearchPlaceholder}
        stickySearchPlaceholder={profileStickySearchPlaceholder}
        searchCollapseStart={searchCollapseStart}
        heroRef={heroRef}
        searchRowRef={searchRowRef}
        mobileScrollY={mobileScrollY}
        mobileCoverHeight={PROFILE_COVER_HEIGHT_MOBILE}
        shopListingsCount={totalListingsCount}
        mobileHeaderTabsOpacity={mobileHeaderTabsOpacity}
        hidden={isMobile && mobileEditOpen}
      />

      <div className="flex flex-col min-h-screen bg-background profile-container">
        <div
          className={cn(
            "min-h-screen overflow-x-hidden md:pb-10",
            isMobile && mobileEditOpen
              ? "pb-[max(1.5rem,env(safe-area-inset-bottom))]"
              : "pb-[calc(3.5rem+var(--kraahl-safe-bottom))]",
          )}
        >
          <div className="mx-auto w-full max-w-(--route-max-width,64rem) pt-0 md:pt-2">
            <div className={cn("relative -mx-0 md:mx-0", isMobile && !mobileEditOpen && "-mt-11")}>
              {!mobileEditOpen ? (
                <>
                  <ProfileCoverBand
                    bannerUrl={bannerUrl}
                    placeholderGradientReady={coverPlaceholderReady}
                    className="rounded-none border-x-0"
                    scrollY={mobileScrollY}
                    collapseOnScroll={isMobile}
                    baseHeight={isMobile ? PROFILE_COVER_HEIGHT_MOBILE : PROFILE_COVER_HEIGHT_DESKTOP}
                  />
                  <div ref={heroRef} className="relative px-4">
                    <ProfileHero
                      displayName={primaryName}
                      handle={username}
                      country={countryHint}
                      bio={bio}
                      avatarUrl={userImage}
                      listingsCount={totalListingsCount}
                      salesCount={salesCount}
                      followersCount={followersCount}
                      isOwner={isOwner}
                      isFollowing={isFollowing}
                      currentUserId={user?.id ?? null}
                      profileId={initialProfile.id}
                      onFollowClick={handleFollowClick}
                      onMessageClick={isOwner ? undefined : handleMessageClick}
                      onNotificationClick={isOwner ? undefined : handleNotificationClick}
                      isSubscribed={isSubscribed}
                      isLoadingSubscription={isLoadingSubscription}
                      hideFollowButton={!!initialProfile.banned_at && !isFollowing}
                      avatarOverlapping
                      scrollY={mobileScrollY}
                      avatarScaleOnScroll={isMobile}
                      onEditClick={isOwner && isMobile ? handleOpenMobileEdit : undefined}
                    />
                  </div>
                </>
              ) : null}
            </div>

            <div className="px-4">
            {!mobileEditOpen ? (
              <ProfileStoreNotice
                note={checkoutNote}
                isOwner={isOwner}
                editDisabled={!!initialProfile.banned_at}
              />
            ) : null}

            <div ref={searchRowRef} className={cn("mb-3", (isMobile || mobileEditOpen) && "hidden")}>
              <ProfileSearchField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={profileListingsSearchPlaceholder}
                variant="compact"
              />
            </div>

            {initialProfile.banned_at ? (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-4 py-6 text-center">
                <ShieldAlert className="h-12 w-12 mx-auto text-amber-600 dark:text-amber-400 mb-3" />
                <p className="text-lg font-semibold text-amber-800 dark:text-amber-200">{tProfile("banished")}</p>
                <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">{tProfile("banishedDesc")}</p>
              </div>
            ) : !mobileEditOpen && user && !isOwner && isFollowing ? (
              <FollowSuggestionsCarousel
                currentUserId={user.id}
                excludeProfileId={initialProfile.id}
              />
            ) : null}

            <div className="mt-2" ref={tabsRowRef}>
              {isMobile && mobileEditOpen && isOwner && user ? (
                <ProfileEditForm
                  variant="inline"
                  userId={user.id}
                  initialUsername={username}
                  initialBio={bio}
                  initialCheckoutNote={checkoutNote}
                  initialAvatarUrl={userImage}
                  initialBannerUrl={bannerUrl}
                  banned={!!initialProfile.banned_at}
                  onClose={handleCloseMobileEdit}
                  onProfileUpdated={handleInlineProfileUpdated}
                />
              ) : (
                <ProfileTabs
                  listings={profileTabListings}
                  collections={filteredCollections}
                  currentUserId={user?.id}
                  profileUsername={username}
                  isOwner={isOwner}
                  sellerBanned={!!initialProfile.banned_at}
                  onCollectionTitleChange={handleCollectionTitleChange}
                  onCollectionDescriptionChange={handleCollectionDescriptionChange}
                  onListingRemovedFromCollection={handleListingRemovedFromCollection}
                  onCollectionCreated={handleCollectionCreated}
                  onCollectionDeleted={isOwner ? handleCollectionDeleted : undefined}
                  onAddItemsToCollection={isOwner ? handleAddItemsToCollection : undefined}
                  hasMoreListings={listingsHasMore && !normalizedSearch}
                  onLoadMoreListings={loadMoreListings}
                  isLoadingMoreListings={isLoadingMoreListings}
                  searchActive={normalizedSearch.length > 0}
                  totalListingsCount={totalListingsCount}
                  tabsRef={tabsRowRef}
                  mobileHeaderTabsOpacity={mobileHeaderTabsOpacity}
                />
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Components */}
      {user && user.id !== initialProfile.id && (
        isMobile ? (
          <ChatTabSlider
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            currentUserId={user.id}
            mode={chatMode === "direct" ? "chat" : "inbox"}
            roomName={chatMode === "direct" ? chatRoomName : undefined}
            username={chatMode === "direct" ? initialProfile.id : undefined}
            initialOtherProfile={chatMode === "direct" ? {
              username: username,
              avatar_url: userImage
            } : undefined}
            counterpartyBanned={!!initialProfile.banned_at}
          />
        ) : (
          <ChatDialog
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            roomName={chatRoomName}
            username={user.id}
            counterpartyBanned={!!initialProfile.banned_at}
          />
        )
      )}

      <DialogCard
        open={showImageErrorModal}
        // The chrome close now runs the same handler the hand-rolled one did —
        // dismissing via Escape/backdrop used to leave `imageError` set.
        onOpenChange={(next) => {
          if (!next) handleCloseImageErrorModal();
        }}
        title="Upload issue"
        panelClassName="sm:max-w-md"
      >
        <div className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20">
            <AlertTriangle className="h-6 w-6 text-amber-500" aria-hidden="true" />
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            {imageError ?? "An unexpected error occurred while uploading your image."}
          </DialogDescription>
        </div>
      </DialogCard>

      {/* Close-only: `ModerationNotice` opens with its own heading. Twin of the
          identical dialog in `my-listings/my-listings.tsx`. */}
      <DialogCard
        open={isCollectionErrorOpen}
        onOpenChange={setIsCollectionErrorOpen}
        chrome="closeOnly"
        a11yTitle="Moderation warning"
        panelClassName="sm:max-w-md"
      >
        <ModerationNotice message={collectionDialogMessage} />
      </DialogCard>

      {/* Profile Notification Modal - seulement pour les utilisateurs connectés */}
      {user && (
        <ProfileNotificationModal
          open={isNotificationModalOpen}
          onClose={handleCloseNotificationModal}
          onConfirm={handleConfirmNotification}
          profile={{
            id: initialProfile.id,
            username: username,
            avatar_url: userImage,
          }}
          mode={notificationModalMode}
        />
      )}
    </>
  );
}
